import React, { Suspense, lazy } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './config/queryClient';
import { LanguageProvider } from './contexts/LanguageContext';
import { AppNoticeProvider } from './contexts/AppNoticeContext';
import { AuthProvider, useAuth } from './auth/authContext';
import { ProtectedRoute } from './auth/ProtectedRoute';
import { AdminLayout } from './layouts/AdminLayout';
import { getDefaultPathForRole } from './config/sidebar';
import './index.css';
import './styles/responsive-global.css';
import './styles/admin-tables-mobile.css';
import './styles/mobile-ios-touch.css';
import './components/admin/AdminModalOverlay.css';
import './styles/global-scrollbars.css';
import './styles/global-selects.css';
import './styles/admin-dark-theme.css';
import { initOfflineSync } from './services/offlineOrders';
import { getBusinessTypes } from './services/businessTypesApi';
import App from './App';
import ContactPage from './pages/ContactPage';
import LegalDocumentPage from './pages/LegalDocumentPage';
import BranchShopPage from './pages/BranchShopPage';
import DesktopLoginPage from './pages/DesktopLoginPage';
import { getMarketingShopSubdomainFromHost } from './utils/marketingShopHost';
import { startDesktopPrinterMonitor } from './utils/desktopElectronPrinter';
import { startDesktopWebAutoUpdate } from './utils/desktopWebAutoUpdate';
import { PublicSeoProvider } from './seo/PublicSeoContext';
import { PublicSeoHead } from './seo/PublicSeoHead';

/** `type="number"` fokusda scroll qiymatni o'zgartirmasligi uchun (kassa, ombor, sozlamalar). */
if (typeof document !== 'undefined') {
  document.addEventListener(
    'wheel',
    (e) => {
      const el = e.target;
      if (el instanceof HTMLInputElement && el.type === 'number') {
        el.blur();
      }
    },
    { passive: true },
  );
}

function ShopOrLanding() {
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  if (getMarketingShopSubdomainFromHost(host)) {
    return <BranchShopPage />;
  }
  return <App />;
}

initOfflineSync();

/** Asosiy sahifa: biznes turlari API birinchi bo‘yog‘dan oldin boshlanadi (hero karusel kechikmasin). */
if (typeof window !== 'undefined') {
  const path = window.location.pathname || '';
  if (path === '/' || path === '') {
    void getBusinessTypes();
  }
}

if (typeof navigator !== 'undefined' && /addedesktop/i.test(String(navigator.userAgent || ''))) {
  startDesktopPrinterMonitor(45000);
  startDesktopWebAutoUpdate();
}

const AdminPageByPath = lazy(() =>
  import('./pages/admin/AdminPageByPath').then((m) => ({ default: m.AdminPageByPath }))
);

function AdminIndexRedirect() {
  const { user } = useAuth();
  const role = user?.role ?? 'platform_owner';
  const defaultPath = getDefaultPathForRole(role);
  return <Navigate to={`/admin/${defaultPath}`} replace />;
}

/** Eski bildirishnoma havolalari `/support/:id` — SPA da marshrut yo‘q edi (bo‘sh sahifa). */
function SupportTenantRedirect() {
  const { tenantId } = useParams();
  const id = tenantId?.trim() ?? '';
  if (!id || !/^\d+$/.test(id)) {
    return <Navigate to="/admin" replace />;
  }
  return <Navigate to={`/admin/tenant-messages?tenant=${encodeURIComponent(id)}`} replace />;
}

function SupportInboxRedirect() {
  return <Navigate to="/admin/help" replace />;
}

function RedirectTermsToOffer() {
  const { lang } = useParams();
  const code = lang && ['uz', 'ru', 'en'].includes(lang) ? lang : 'uz';
  return <Navigate to={`/offer/${code}`} replace />;
}

function PageLoader() {
  return (
    <div className="admin-page-skeleton" style={{ padding: '24px', minHeight: '50vh' }}>
      <div className="skeleton-box" style={{ width: '40%', height: 28, marginBottom: 16, borderRadius: 6 }} />
      <div className="skeleton-box" style={{ width: '60%', height: 18, marginBottom: 24, borderRadius: 4 }} />
      <div className="skeleton-box" style={{ width: '100%', height: 200, borderRadius: 8 }} />
    </div>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <AppNoticeProvider>
        <AuthProvider>
          <BrowserRouter>
          <PublicSeoProvider>
          <PublicSeoHead />
          <Routes>
            <Route path="/" element={<ShopOrLanding />} />
            <Route path="/desktop-login" element={<DesktopLoginPage />} />
            <Route path="/login" element={<DesktopLoginPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/privacy" element={<Navigate to="/privacy/uz" replace />} />
            <Route path="/privacy/:lang" element={<LegalDocumentPage docType="privacy" />} />
            <Route path="/offer" element={<Navigate to="/offer/uz" replace />} />
            <Route path="/terms" element={<Navigate to="/offer/uz" replace />} />
            <Route path="/offer/:lang" element={<LegalDocumentPage docType="offer" />} />
            <Route path="/terms/:lang" element={<RedirectTermsToOffer />} />
            <Route path="/s/:branchSlug" element={<BranchShopPage />} />
            <Route path="/shop/:branchSlug" element={<BranchShopPage />} />
            <Route
              path="/support/:tenantId"
              element={
                <ProtectedRoute>
                  <SupportTenantRedirect />
                </ProtectedRoute>
              }
            />
            <Route
              path="/support"
              element={
                <ProtectedRoute>
                  <SupportInboxRedirect />
                </ProtectedRoute>
              }
            />
            <Route path="admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
              <Route index element={<AdminIndexRedirect />} />
              <Route
                path=":pagePath"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <AdminPageByPath />
                  </Suspense>
                }
              />
            </Route>
          </Routes>
          </PublicSeoProvider>
          </BrowserRouter>
        </AuthProvider>
        </AppNoticeProvider>
      </LanguageProvider>
    </QueryClientProvider>
  </React.StrictMode>
);

